import { useEffect, useState } from 'react'
import { doc, onSnapshot } from 'firebase/firestore'
import { db } from '@/lib/firebase'
import type { LetterConfig, LetterPack } from '@/types/models'

const FALLBACK: LetterConfig = {
  enabled: false,
  packs: [],
  templateVersion: 'unknown',
  whitelistUids: [],
  upsell: {
    headline: 'Send a physical letter',
    body: 'A printed letter, posted via Australia Post, makes your Biddle stand out — and helps reach owners who aren’t on Biddle yet.',
  },
}

function parsePack(raw: unknown): LetterPack | null {
  if (!raw || typeof raw !== 'object') return null
  const p = raw as Record<string, unknown>
  if (typeof p.sku !== 'string') return null
  const priceCents = typeof p.priceCents === 'number' ? p.priceCents : Number(p.priceCents)
  const letterCount = typeof p.letterCount === 'number' ? p.letterCount : Number(p.letterCount)
  if (!Number.isFinite(priceCents) || !Number.isFinite(letterCount)) return null
  return { sku: p.sku, priceCents, letterCount }
}

function parseConfig(data: Record<string, unknown>): LetterConfig {
  const packsRaw = Array.isArray(data.packs) ? data.packs : []
  const packs = packsRaw.map(parsePack).filter((p): p is LetterPack => p != null)
  const upsell =
    data.upsell && typeof data.upsell === 'object'
      ? (data.upsell as Record<string, unknown>)
      : null

  return {
    enabled: Boolean(data.enabled),
    packs,
    templateVersion: typeof data.templateVersion === 'string' ? data.templateVersion : 'unknown',
    whitelistUids: Array.isArray(data.whitelistUids)
      ? data.whitelistUids.filter((u): u is string => typeof u === 'string')
      : [],
    upsell: {
      headline:
        typeof upsell?.headline === 'string' ? upsell.headline : FALLBACK.upsell.headline,
      body: typeof upsell?.body === 'string' ? upsell.body : FALLBACK.upsell.body,
    },
  }
}

export function useLetterConfig() {
  const [config, setConfig] = useState<LetterConfig>(FALLBACK)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const unsub = onSnapshot(
      doc(db, 'config', 'letters'),
      (snap) => {
        if (!snap.exists()) {
          setConfig(FALLBACK)
        } else {
          setConfig(parseConfig(snap.data() as Record<string, unknown>))
        }
        setLoading(false)
        setError(null)
      },
      (err) => {
        setError(err.message)
        setConfig(FALLBACK)
        setLoading(false)
      },
    )
    return () => unsub()
  }, [])

  return { config, loading, error }
}
